import { useQueryClient } from "@tanstack/react-query";
import { isBefore, set, startOfDay } from "date-fns";
import React, { useEffect, useRef, useState } from "react";

import { ButtonStandard, Text } from "@performyard/py-components";
import type { EmployeeSearchServiceQueryParams } from "@performyard/service-contracts";
import type { WorkspaceModelJson } from "@performyard/service-contracts";

import EmployeePicker, { type SingleEmployee } from "../../../../components/EmployeePicker";
import DatePicker from "../../../../components/Inputs/DatePicker";
import InputWrapper from "../../../../components/Inputs/InputWrapper";
import ContentModal from "../../../../components/Modal/Templates/ContentModal";
import Spacer from "../../../../components/Spacer";
import { StaticToast } from "../../../../components/Toast";

import { type AssignmentValidationErrors, type UserAssignmentContext, assignmentFormSchema } from "../types";

import { useEditMeetingActionItem } from "../../../../api/hooks";
import { useEmployeePickerSearch } from "../../../../api/hooks/defs/employees";
import useForm from "../../../../hooks/useForm";

import { config } from "../config";
import { meetingDetailsQueryKeys } from "../query-keys";

type UserAssignmentModalProps = {
	isOpen: boolean;
	onClose: () => void;
	assignmentContext: UserAssignmentContext;
	assignerId: string;
	workspace: WorkspaceModelJson;
};

/**
 * Modal for assigning action items to users and setting due dates.
 */
const UserAssignmentModal = ({
	isOpen,
	onClose,
	assignmentContext,
	assignerId,
	workspace,
}: UserAssignmentModalProps): React.ReactElement => {
	const { mutate: editActionItem, isPending: isEditActionItemPending } = useEditMeetingActionItem();
	const queryClient = useQueryClient();
	const hasInitialized = useRef(false);
	const initialDueDateRef = useRef<Date | null>(null);
	const [validationErrors, setValidationErrors] = useState<AssignmentValidationErrors>({});

	const workspaceParticipantIds = workspace.participants.map((participant) => participant.userId);

	const searchServiceParams: Partial<EmployeeSearchServiceQueryParams> = {
		id_in: workspaceParticipantIds as unknown as EmployeeSearchServiceQueryParams["id_in"],
	};

	const { employeeData } = useEmployeePickerSearch(
		"employee-by-id",
		{
			searchTerm: "",
			excludeCurrentUser: false,
			exclusionList: [],
		},
		null,
		"searchService",
		searchServiceParams
	);

	const { validate, updateForm, state } = useForm({
		schema: assignmentFormSchema,
		defaultValues: {
			assignee: null,
			dueDate: null,
		},
		disableWarning: true,
	});

	useEffect(() => {
		if (isOpen && assignmentContext && !hasInitialized.current) {
			if (employeeData?.length) {
				if (assignmentContext.currentAssigneeId) {
					const currentAssigneeData = employeeData.find(
						(emp) => emp.value === assignmentContext.currentAssigneeId
					);
					if (currentAssigneeData) {
						updateForm({ assignee: currentAssigneeData });
					}
				} else {
					updateForm({ assignee: null });
				}

				if (assignmentContext.dueDate) {
					const existingDueDate = set(assignmentContext.dueDate, config.assignment.defaultDueTime);
					updateForm({ dueDate: existingDueDate });
					initialDueDateRef.current = existingDueDate;
				} else {
					updateForm({ dueDate: null });
					initialDueDateRef.current = null;
				}
			}

			hasInitialized.current = true;
		} else if (!isOpen) {
			hasInitialized.current = false;
		}
	}, [isOpen, assignmentContext, employeeData, updateForm]);

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
		e.preventDefault();
		setValidationErrors({});

		const validationResult = validate();
		if (!validationResult.isValid) {
			const errors: AssignmentValidationErrors = {};
			Object.entries(validationResult.errors || {}).forEach(([key, error]) => {
				errors[key as keyof AssignmentValidationErrors] = { message: error.message };
			});
			setValidationErrors(errors);
			return;
		}

		if (assignmentContext) {
			const dueDateChanged = state.dueDate?.getTime() !== initialDueDateRef.current?.getTime();

			editActionItem(
				{
					actionItemId: assignmentContext.actionItemId,
					data: {
						participants: state.assignee
							? [
									{ userId: assignerId, role: "assigner" },
									{ userId: state.assignee.value, role: "assignee" },
								]
							: [],
						...(dueDateChanged && { dueDate: state.dueDate ? state.dueDate.toISOString() : null }),
					},
				},
				{
					onSuccess: () => {
						queryClient.invalidateQueries({
							queryKey: meetingDetailsQueryKeys.meetingActionItems(assignmentContext.meetingId),
						});
						onClose();
					},
				}
			);
		}
	};

	const handleClose = (): void => {
		setValidationErrors({});
		onClose();
	};

	return (
		<ContentModal
			isOpen={isOpen}
			size="large"
			title={
				<Text size="sub-heading" weight="semi-bold">
					Assign Action Item
				</Text>
			}
			handleClose={handleClose}
			buttons={[
				<div key="cancelButton" className="w-fit">
					<ButtonStandard size="large" onClick={handleClose} disabled={isEditActionItemPending}>
						Cancel
					</ButtonStandard>
				</div>,
				<div key="assignButton" className="w-fit">
					<ButtonStandard
						size="large"
						color="green"
						type="submit"
						form="assignment-form"
						disabled={isEditActionItemPending}
					>
						Assign
					</ButtonStandard>
				</div>,
			]}
		>
			<form id="assignment-form" onSubmit={handleSubmit}>
				<EmployeePicker
					value={state.assignee || ""}
					onChange={(val) => updateForm({ assignee: val as SingleEmployee | null })}
					disabled={isEditActionItemPending}
					isClearable={true}
					filters={{ id_in: workspaceParticipantIds.join(",") }}
					version="searchService"
					searchServiceProps={searchServiceParams}
					label="Assignee"
					errorMessage={validationErrors.assignee?.message}
				/>
				<Spacer height={2} />
				<InputWrapper label="Due Date" errorMessage={validationErrors.dueDate?.message}>
					<DatePicker
						value={state.dueDate || ""}
						onChange={(date) => {
							if (date) {
								if (isBefore(startOfDay(date), startOfDay(new Date()))) {
									setValidationErrors((prev) => ({
										...prev,
										dueDate: { message: "Due date must not be in the past" },
									}));
									return;
								}
								setValidationErrors((prev) => {
									const { dueDate: _, ...rest } = prev;
									return rest;
								});
								updateForm({
									dueDate: set(date, config.assignment.defaultDueTime),
								});
							} else {
								setValidationErrors((prev) => {
									const { dueDate: _, ...rest } = prev;
									return rest;
								});
								updateForm({ dueDate: null });
							}
						}}
						disabled={isEditActionItemPending}
						clearable={true}
						hasIcon={true}
					/>
				</InputWrapper>
				{state.dueDate && (
					<>
						<Spacer height={2} />
						<div className="flex items-center justify-center">
							<StaticToast
								message={`Due Date is set for ${config.assignment.dueTimeLabel} in your current time zone`}
								type="info"
								size="small"
							/>
						</div>
					</>
				)}
			</form>
		</ContentModal>
	);
};

export default UserAssignmentModal;
